import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useFragrances } from '../../contexts/FragranceContext';
import Header from '../layout/Header';
import SearchAndFilter from '../fragrance/SearchAndFilter';
import FragranceGrid from '../fragrance/FragranceGrid';
import FragranceForm from '../fragrance/FragranceForm';
import Button from '../ui/Button';
import { Fragrance } from '../fragrance/FragranceCard';

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const isDemoUser = user?.username === 'Demo';
  const {
    collection,
    stats,
    isLoading,
    error,
    filters,
    sorting,
    fetchFragrances,
    fetchStats,
    deleteFragrance,
    toggleFavorite,
    copyFragranceDetails
  } = useFragrances();

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingFragrance, setEditingFragrance] = useState<Fragrance | null>(null);

  const { token } = useAuth();

  // Load initial data
  useEffect(() => {
    if (token) {
      fetchFragrances();
      fetchStats();
    }
  }, [token, fetchFragrances, fetchStats]); // Empty dependency array to run only once 

  const handleEdit = (fragrance: Fragrance) => {
    if (isDemoUser) return;
    setEditingFragrance(fragrance);
  };

  const handleDelete = async (fragranceId: number) => {
    if (isDemoUser) return;
    const result = await deleteFragrance(fragranceId);
    if (result.success) {
      // Success feedback could be added here (toast notification)
      console.log('Fragrance deleted successfully');
    } else {
      console.error('Failed to delete fragrance:', result.message);
    }
  };

  const handleToggleFavorite = async (fragranceId: number) => {
    if (isDemoUser) return;
    const result = await toggleFavorite(fragranceId);
    if (result.success) {
      console.log('Favorite status toggled');
    } else {
      console.error('Failed to toggle favorite:', result.message);
    }
  };

  const handleCopyDetails = (fragrance: Fragrance) => {
    copyFragranceDetails(fragrance);
    // TODO: Add toast notification for copy success
    console.log('Fragrance details copied to clipboard');
  };

  const handleAddNew = () => {
    if (isDemoUser) return;
    setShowAddForm(true);
  };

  const handleFormClose = () => {
    if (isDemoUser) return;
    setShowAddForm(false);
    setEditingFragrance(null);
  };

  const handleFormSuccess = () => {
    if (isDemoUser) return;
    fetchStats(); // Refresh stats after adding/editing
  };

  interface PaginationProps {
    page: number;
    totalPages: number;
    onChangePage: (pageNum: number) => void;
  }
  
  const PaginationControls: React.FC<PaginationProps> = ({ page, totalPages, onChangePage }) => {
    return (
      <>
        {/* Previous button */}
        <button
          onClick={() => onChangePage(page - 1)}
          disabled={page === 1}
          className="px-3 py-1 rounded border text-sm disabled:opacity-50"
        >
          Previous
        </button>
  
        {/* Numbered page buttons */}
        {[...Array(totalPages)].map((_, idx) => {
          const pageNum = idx + 1;
          return (
            <button
              key={pageNum}
              onClick={() => onChangePage(pageNum)}
              className={`px-3 py-1 rounded border text-sm ${
                page === pageNum
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300'
              }`}
            >
              {pageNum}
            </button>
          );
        })}
  
        {/* Next button */}
        <button
          onClick={() => onChangePage(page + 1)}
          disabled={page === totalPages}
          className="px-3 py-1 rounded border text-sm disabled:opacity-50"
        >
          Next
        </button>
      </>
    );
  };

  return (
    <div>
      {/* Add Fragrance Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 flex justify-end"
      >
        <Button
          onClick={handleAddNew}
          disabled={isDemoUser} // disable if demo user
          title={isDemoUser ? 'Unavailable in Demo' : undefined} // helper text
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Fragrance
        </Button>
      </motion.div>
  
      {/* Collection Stats */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {stats.totals.fragrances}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Fragrances</div>
          </div>
  
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              {stats.totals.decants}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Decants & Samples</div>
          </div>
  
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-3xl font-bold text-red-600 dark:text-red-400">
              {stats.totals.favorites}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Favorites</div>
          </div>
  
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">
              {stats.totals.houses}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Unique Houses</div>
          </div>
        </motion.div>
      )}
  
      {/* Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
        >
          <div className="flex">
            <svg
              className="w-5 h-5 text-red-400 mr-3 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Error loading collection
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
            </div>
          </div>
        </motion.div>
      )}
  
      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <SearchAndFilter />
      </motion.div>
  
      {/* Collection Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        {/* Header + Top Pagination */}
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          {/* Title */}
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center sm:text-left">
            Your Collection
            {collection && (
              <span className="ml-2 text-sm font-normal text-gray-600 dark:text-gray-400">
                ({collection.fragrances.length} of {collection.pagination.total})
              </span>
            )}
          </h2>
  
          {/* Top Pagination */}
          {collection && collection.pagination.totalPages > 1 && (
            <div className="flex justify-center sm:justify-end flex-wrap gap-2">
              {collection.pagination.page > 1 && (
                <button
                  onClick={() => fetchFragrances(filters, sorting, collection.pagination.page - 1)}
                  className="px-3 py-1 rounded border text-sm"
                >
                  Previous
                </button>
              )}
  
              {[...Array(collection.pagination.totalPages)].map((_, idx) => {
                const pageNum = idx + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => fetchFragrances(filters, sorting, pageNum)}
                    className={`px-3 py-1 rounded border text-sm ${
                      collection.pagination.page === pageNum
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
  
              {collection.pagination.page < collection.pagination.totalPages && (
                <button
                  onClick={() => fetchFragrances(filters, sorting, collection.pagination.page + 1)}
                  className="px-3 py-1 rounded border text-sm"
                >
                  Next
                </button>
              )}
            </div>
          )}
        </div>
  
        {/* Grid */}
        <FragranceGrid
          fragrances={collection?.fragrances || []}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleFavorite={handleToggleFavorite}
          onCopyDetails={handleCopyDetails}
        />
  
        {/* Bottom Pagination */}
        {collection && collection.pagination.totalPages > 1 && (
          <div className="mt-6 flex justify-center flex-wrap gap-2">
            {collection.pagination.page > 1 && (
              <button
                onClick={() => fetchFragrances(filters, sorting, collection.pagination.page - 1)}
                className="px-3 py-1 rounded border text-sm"
              >
                Previous
              </button>
            )}
  
            {[...Array(collection.pagination.totalPages)].map((_, idx) => {
              const pageNum = idx + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => fetchFragrances(filters, sorting, pageNum)}
                  className={`px-3 py-1 rounded border text-sm ${
                    collection.pagination.page === pageNum
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
  
            {collection.pagination.page < collection.pagination.totalPages && (
              <button
                onClick={() => fetchFragrances(filters, sorting, collection.pagination.page + 1)}
                className="px-3 py-1 rounded border text-sm"
              >
                Next
              </button>
            )}
          </div>
        )}
      </motion.div>
  
      {/* Add/Edit Fragrance Form */}
      {(showAddForm || editingFragrance) && (
        <FragranceForm
          fragrance={editingFragrance}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );  
};

export default Dashboard;
